import type { DsdEditorCompanyNameReference } from "@/apis/dsd-editor/route";
import { buildCompanyNameRepresentativeByOccurrenceId } from "@/utils/dsd-editor/company-names";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
  companyNameReviewToggleReducer,
  INITIAL_COMPANY_NAME_REVIEW_TOGGLE,
} from "../company-name-review-toggle";

/**
 * 기업명 확인 탭의 선택 상태.
 * 패널은 대표명 목록 ↔ 대표명 상세(표기 목록) 2단 — 상세 진입은 패널에서 대표명 클릭, 또는 본문 하이라이트 클릭.
 * 표기를 고를 때마다(패널·본문) onSelect 를 부른다 (합계·교정 훅과 같은 계약)
 */
export const useDsdReviewCompanyNameReview = ({
  enabled,
  references,
  requestCellScroll,
  onSelect,
}: {
  enabled: boolean;
  references: DsdEditorCompanyNameReference[];
  requestCellScroll: (cellId: string) => void;
  /** 표기를 고를 때마다 — 다른 선택 지우기·패널 열기 등 */
  onSelect?: () => void;
}) => {
  const [selectedRepresentative, setSelectedRepresentative] = useState<
    string | null
  >(null);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<
    string | null
  >(null);
  // 검토 항목만 보기 — 목록·상세 공유, 본문 클릭 진입 시 상세에서만 임시 OFF (규칙은 reducer 주석)
  const [toggle, dispatchToggle] = useReducer(
    companyNameReviewToggleReducer,
    INITIAL_COMPANY_NAME_REVIEW_TOGGLE,
  );
  // 상세 → 목록 (뒤로·빈 곳 클릭·탭 이동 공통). 임시 OFF였다면 토글을 원래 값으로
  const leaveDetail = useCallback(() => {
    setSelectedRepresentative(null);
    setSelectedOccurrenceId(null);
    dispatchToggle({ type: "leave-detail" });
  }, []);
  const representativeByOccurrenceId = useMemo(
    () => buildCompanyNameRepresentativeByOccurrenceId(references),
    [references],
  );
  // 패널 표기 클릭 — 선택 + 해당 위치로 스크롤
  const selectOccurrence = useCallback(
    (occurrenceId: string) => {
      onSelect?.();
      setSelectedOccurrenceId(occurrenceId);
      requestCellScroll(occurrenceId);
    },
    [onSelect, requestCellScroll],
  );
  // 본문 하이라이트 클릭 — 선택 + 그 대표명 상세로 (이미 그 위치라 스크롤 없음)
  const selectOccurrenceFromDocument = useCallback(
    (occurrenceId: string) => {
      onSelect?.();
      setSelectedOccurrenceId(occurrenceId);
      setSelectedRepresentative(
        representativeByOccurrenceId.get(occurrenceId) ?? null,
      );
      dispatchToggle({ type: "enter-from-document" });
    },
    [onSelect, representativeByOccurrenceId],
  );
  const clearOccurrence = useCallback(() => setSelectedOccurrenceId(null), []);
  const setNeedsConfirmationOnly = useCallback(
    (value: boolean) => dispatchToggle({ type: "set", value }),
    [],
  );

  useEffect(() => {
    if (!enabled) leaveDetail();
  }, [enabled, leaveDetail]);

  return {
    selectedRepresentative,
    selectRepresentative: setSelectedRepresentative,
    selectedOccurrenceId,
    needsConfirmationOnly: toggle.needsConfirmationOnly,
    setNeedsConfirmationOnly,
    selectOccurrence,
    selectOccurrenceFromDocument,
    clearOccurrence,
    leaveDetail,
  };
};
